import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

const formatContent = (val) => {
  if (!val || val === '{}' || val === '[]') return '';
  let parsedVal = val;
  if (typeof val === 'string') {
    try {
      const trimmed = val.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        let toParse = trimmed.replace(/,\s*([\]\}])/g, '$1');
        parsedVal = JSON.parse(toParse);
      }
    } catch(e) {
      // Fallback for malformed JSON string, strip outer brackets and quotes
      parsedVal = val.trim()
        .replace(/^[\s\[\{]+|[\s\]\}]+$/g, '')
        .replace(/",?\s*\n\s*"?/g, '<br />')
        .replace(/",\s*"/g, '<br />')
        .replace(/["']/g, '');
    }
  }

  if (typeof parsedVal === 'object' && parsedVal !== null && !Array.isArray(parsedVal)) {
    try {
      return Object.entries(parsedVal).map(([k, v]) => `<strong class="text-gray-900 font-semibold">${k}:</strong> ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('<br />');
    } catch(e) {
      return JSON.stringify(parsedVal);
    }
  }

  let str = Array.isArray(parsedVal) ? parsedVal.join('<br />') : String(parsedVal);
  str = str.replace(/\n/g, '<br />');

  // Bold titles at the beginning or after a period.
  str = str.replace(/(^|\.\s+)([A-Z][A-Za-z\s\-,\&]{0,49}[A-Za-z]):\s/g, (match, prefix, title) => {
    if (prefix.includes('.')) {
      return `.<br /><br /><strong class="text-gray-900 font-semibold">${title}:</strong> `;
    }
    return `<strong class="text-gray-900 font-semibold">${title}:</strong> `;
  });

  // Also bold titles that are right after a <br />
  str = str.replace(/(\<br \/\>\s*)([A-Z][A-Za-z\s\-,\&]{0,49}[A-Za-z]):\s/g, (match, prefix, title) => {
    return `${prefix}<strong class="text-gray-900 font-semibold">${title}:</strong> `;
  });

  return str;
};

const SignOffConfirmation = ({ isOpen, onClose, onConfirm, isLoading, fields, setFields, jsonData, summaryjson }) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');

  if (!isOpen) return null;

  const handleStartEdit = (section) => {
    const sectionKey = section.placeholder?.toLowerCase();
    const summaryValue = summaryjson?.[sectionKey] || '';
    const fieldValue = fields?.[section.key] || '';
    const currentValue = fieldValue || summaryValue || '';
    
    let valueToEdit = '';
    if (typeof currentValue === 'object' && currentValue !== null) {
      valueToEdit = JSON.stringify(currentValue, null, 2);
    } else {
      valueToEdit = String(currentValue);
    }

    setEditValue(valueToEdit);
    setEditingSection(section.key);
  };

  const handleSaveEdit = (sectionKey) => {
    if (setFields) {
      setFields(prev => ({ ...prev, [sectionKey]: editValue }));
    }
    setEditingSection(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditValue('');
  };

  return (
    <>
      {/* Dark Background Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
        onClick={onClose}
      />

      {/* Confirmation Modal */}
      <div
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[61]
          bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1200px] max-h-[85vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#1C2A3A] to-[#2D4A6A]">
          <div>
            <h2 className="text-lg font-semibold text-white">Confirm Sign Off</h2>
            <p className="text-xs text-gray-300 mt-0.5">Review and edit your note before signing off</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-300 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Note Preview Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar bg-gray-50/50">
          {jsonData && jsonData.length > 0 ? (
            <div className="columns-1 md:columns-2 gap-6">
              {jsonData.map((section, idx) => {
              const sectionKey = section.placeholder?.toLowerCase();
              const summaryValue = summaryjson?.[sectionKey] || '';
              const fieldValue = fields?.[section.key] || '';
              const displayValue = fieldValue || summaryValue;
              const isEditing = editingSection === section.key;
              
              if (!displayValue || 
                  (typeof displayValue === 'string' && (displayValue.trim().length === 0 || displayValue.trim() === '{}' || displayValue.trim() === '[]')) ||
                  (typeof displayValue === 'object' && Object.keys(displayValue).length === 0)) {
                return null;
              }

              return (
                <div key={idx} className="mb-6 break-inside-avoid bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-[#1C2A3A] capitalize">
                        {section.label
                          ? section.label.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
                          : ''}
                      </h3>
                      {['review of systems', 'physical examination'].includes(section.label) && summaryValue && (
                        <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full font-medium ${
                          (summaryValue || '').replace(/\s|\./g, '') === 'Normal'
                            ? 'text-green-700 bg-green-100'
                            : 'text-red-700 bg-red-100'
                        }`}>
                          {(summaryValue || '').replace(/\s|\./g, '') === 'Normal' ? 'Normal' : 'Abnormal'}
                        </span>
                      )}
                    </div>

                    {/* Edit / Save buttons */}
                    {!isEditing ? (
                      <button
                        onClick={() => handleStartEdit(section)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#64748B] hover:text-[#1C2A3A] hover:bg-gray-100 rounded-md transition-colors"
                        title="Edit this section"
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                        <span>Edit</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSaveEdit(section.key)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-[#10B981] hover:bg-[#059669] rounded-md transition-colors"
                        >
                          <CheckIcon sx={{ fontSize: 14 }} />
                          <span>Save</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Display or Edit */}
                  {isEditing ? (
                    <textarea
                      className="w-full text-sm text-gray-700 leading-relaxed bg-white rounded-lg p-3 border border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y min-h-[100px]"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      autoFocus
                      rows={Math.max(4, editValue.split('\n').length + 1)}
                    />
                  ) : (
                    <div
                      className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
                      onClick={() => handleStartEdit(section)}
                      title="Click to edit"
                      dangerouslySetInnerHTML={{
                        __html: formatContent(displayValue)
                      }}
                    />
                  )}
                </div>
              );
            })
            }
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              No note content available
            </div>
          )}
        </div>

        {/* Footer with Confirm / Cancel */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">
            ⚠️ Once signed off, this note will be marked as approved.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                let latestFields = fields;
                if (editingSection !== null) {
                  latestFields = { ...fields, [editingSection]: editValue };
                  if (setFields) setFields(latestFields);
                  setEditingSection(null);
                  setEditValue('');
                }
                onConfirm(latestFields);
              }}
              disabled={isLoading}
              className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#E17954] hover:bg-[#C0392B] cursor-pointer'
              }`}
            >
              {isLoading ? 'Signing Off...' : 'Confirm Sign Off'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignOffConfirmation;
